import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IndianRupee, Check, Search, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { packages as mockPackages } from '../data/mock';
import { publicAPI } from '../services/api';
import { Input } from '../components/ui/input';

const PackagesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await publicAPI.getPackages();
        if (response.data && response.data.length > 0) {
          setPackages(response.data);
        } else {
          setPackages(mockPackages);
        }
      } catch (error) {
        console.log('Using mock data for packages');
        setPackages(mockPackages);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  // Scroll to section when hash is present
  useEffect(() => {
    if (!loading && location.hash) {
      const elementId = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [loading, location.hash]);

  // Filter to only show main categories
  const categories = ['all', 'Smart Wellness Packages', 'Advanced Wellness Packages'];

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.tests.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || pkg.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => {
    // Sort by sort_order for consistent ordering
    const orderA = a.sort_order ?? 999;
    const orderB = b.sort_order ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    // If same sort_order, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#007897] to-[#006680]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Health Packages</h1>
            <p className="text-blue-100 max-w-2xl mx-auto mb-8">
              Comprehensive health check-up packages designed for preventive healthcare at affordable prices.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm">
              <Link to="/" className="text-blue-200 hover:text-white">Home</Link>
              <span className="text-blue-300">/</span>
              <span>Packages</span>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-white border-b sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search packages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#007897] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All Packages' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages Grid - Segregated by Category */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-[#007897]" size={40} />
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No packages found matching your criteria.</p>
            </div>
          ) : selectedCategory === 'all' ? (
            <>
              {/* Advanced Wellness Packages Section */}
              {(() => {
                const advancedOrder = ['SILVER PACKAGE', 'GOLD PACKAGE', 'PLATINUM PACKAGE', 'RUBY PACKAGE'];
                const advancedPackages = advancedOrder
                  .map(name => filteredPackages.find(pkg => pkg.name === name))
                  .filter(Boolean);
                
                if (advancedPackages.length === 0) return null;
                
                return (
                  <div id="advanced-wellness" className="mb-16 scroll-mt-40">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-3">
                      Advanced Wellness Packages
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {advancedPackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border ${
                            pkg.featured ? 'border-[#007897] ring-2 ring-[#007897]/20' : 'border-gray-100'
                          }`}
                        >
                          {pkg.featured && (
                            <div className="bg-[#007897] text-white text-xs font-medium text-center py-1">
                              Popular Choice
                            </div>
                          )}
                          
                          {/* Header */}
                          <div className="p-6 border-b">
                            <span className="text-xs font-medium text-[#00a0af] uppercase tracking-wider">
                              {pkg.category}
                            </span>
                            <h3 className="text-xl font-bold text-gray-900 mt-1">{pkg.name}</h3>
                            <div className="flex items-baseline gap-2 mt-3">
                              <span className="flex items-center text-3xl font-bold text-[#007897]">
                                <IndianRupee size={24} />
                                {pkg.price.toLocaleString()}
                              </span>
                              <span className="text-gray-400 line-through text-sm">
                                ₹{(pkg.originalPrice || pkg.original_price).toLocaleString()}
                              </span>
                            </div>
                          </div>

                          {/* Tests */}
                          <div className="p-6">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                              {pkg.tests.length} Tests Included
                            </p>
                            <ul className="space-y-2 mb-4">
                              {pkg.tests.slice(0, 5).map((test, index) => (
                                <li key={`item-${index}`} className="flex items-center gap-2 text-sm text-gray-700">
                                  <Check size={14} className="text-green-500 flex-shrink-0" />
                                  {test}
                                </li>
                              ))}
                              {pkg.tests.length > 5 && (
                                <li className="text-sm text-[#007897] font-medium">
                                  +{pkg.tests.length - 5} more tests
                                </li>
                              )}
                            </ul>
                            <Link
                              to={`/packages/${pkg.slug || pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                              className="block w-full text-center py-2 bg-[#007897] text-white rounded-lg hover:bg-[#006680] transition-colors font-medium"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Smart Wellness Packages Section */}
              {(() => {
                const smartPackages = filteredPackages
                  .filter(pkg => pkg.category === 'Smart Wellness Packages')
                  .sort((a, b) => {
                    // Sort by sort_order first, then alphabetically
                    const orderA = a.sort_order ?? 999;
                    const orderB = b.sort_order ?? 999;
                    if (orderA !== orderB) return orderA - orderB;
                    return a.name.localeCompare(b.name);
                  });
                
                if (smartPackages.length === 0) return null;
                
                return (
                  <div id="smart-wellness" className="scroll-mt-40">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-3">
                      Smart Wellness Packages
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {smartPackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border ${
                            pkg.featured ? 'border-[#007897] ring-2 ring-[#007897]/20' : 'border-gray-100'
                          }`}
                        >
                          {pkg.featured && (
                            <div className="bg-[#007897] text-white text-xs font-medium text-center py-1">
                              Popular Choice
                            </div>
                          )}
                          
                          {/* Header */}
                          <div className="p-6 border-b">
                            <span className="text-xs font-medium text-[#00a0af] uppercase tracking-wider">
                              {pkg.category}
                            </span>
                            <h3 className="text-xl font-bold text-gray-900 mt-1">{pkg.name}</h3>
                            <div className="flex items-baseline gap-2 mt-3">
                              <span className="flex items-center text-3xl font-bold text-[#007897]">
                                <IndianRupee size={24} />
                                {pkg.price.toLocaleString()}
                              </span>
                              <span className="text-gray-400 line-through text-sm">
                                ₹{(pkg.originalPrice || pkg.original_price).toLocaleString()}
                              </span>
                            </div>
                          </div>

                          {/* Tests */}
                          <div className="p-6">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                              {pkg.tests.length} Tests Included
                            </p>
                            <ul className="space-y-2 mb-4">
                              {pkg.tests.slice(0, 5).map((test, index) => (
                                <li key={`item-${index}`} className="flex items-center gap-2 text-sm text-gray-700">
                                  <Check size={14} className="text-green-500 flex-shrink-0" />
                                  {test}
                                </li>
                              ))}
                              {pkg.tests.length > 5 && (
                                <li className="text-sm text-[#007897] font-medium">
                                  +{pkg.tests.length - 5} more tests
                                </li>
                              )}
                            </ul>
                            <Link
                              to={`/packages/${pkg.slug || pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                              className="block w-full text-center py-2 bg-[#007897] text-white rounded-lg hover:bg-[#006680] transition-colors font-medium"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border ${
                    pkg.featured ? 'border-[#007897] ring-2 ring-[#007897]/20' : 'border-gray-100'
                  }`}
                >
                  {pkg.featured && (
                    <div className="bg-[#007897] text-white text-xs font-medium text-center py-1">
                      Popular Choice
                    </div>
                  )}
                  
                  {/* Header */}
                  <div className="p-6 border-b">
                    <span className="text-xs font-medium text-[#00a0af] uppercase tracking-wider">
                      {pkg.category}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mt-1">{pkg.name}</h3>
                    <div className="flex items-baseline gap-2 mt-3">
                      <span className="flex items-center text-3xl font-bold text-[#007897]">
                        <IndianRupee size={24} />
                        {pkg.price.toLocaleString()}
                      </span>
                      <span className="text-gray-400 line-through text-sm">
                        ₹{(pkg.originalPrice || pkg.original_price).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Tests */}
                  <div className="p-6">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                      {pkg.tests.length} Tests Included
                    </p>
                    <ul className="space-y-2 mb-4">
                      {pkg.tests.slice(0, 5).map((test, index) => (
                        <li key={`item-${index}`} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check size={14} className="text-green-500 flex-shrink-0" />
                          {test}
                        </li>
                      ))}
                      {pkg.tests.length > 5 && (
                        <li className="text-sm text-[#007897] font-medium">
                          +{pkg.tests.length - 5} more tests
                        </li>
                      )}
                    </ul>
                    <Link
                      to={`/packages/${pkg.slug || pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="block w-full text-center py-2 bg-[#007897] text-white rounded-lg hover:bg-[#006680] transition-colors font-medium"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Why Choose Our Packages?</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'Comprehensive', desc: 'All essential tests in one package' },
              { title: 'Affordable', desc: 'Individual tests at discounted prices bundled in a package' },
              { title: 'Convenient', desc: 'Home collection available' },
              { title: 'Fast Results', desc: 'Reports same day evening' }
            ].map((item, index) => (
              <div key={`item-${index}`} className="text-center p-6 bg-gray-50 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default PackagesPage;
